import { Price, PriceInterval } from '@griply/types';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Logo } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { PaymentOptions } from 'shared/components/ui/payment-options';
import { premiumUsps } from 'shared/constants/premium-usps';
import { ID } from 'shared/types/id';

import { FeatureList } from '../feature-list';
import * as Styled from './lifetime-deal-pricing-card.style';

const NEW_PRICES_URL = 'https://griply.app/pricing/new-prices';

const hiddenUspLabels = ['dialogs.premium.usp.7', 'dialogs.premium.usp.10'];
const lifetimeDealUsps = premiumUsps.filter(
  ({ label }) => !hiddenUspLabels.includes(label),
);

export type LifetimeDealPricingCardProps = {
  options: Price[];
  selectedOption: ID;
  onOption: (id: ID) => void;
  disabled?: boolean;
  cta: React.ReactNode;
};

export const LifetimeDealPricingCard: React.FC<
  LifetimeDealPricingCardProps
> = ({ options, selectedOption, onOption, disabled, cta }) => {
  const { t } = useTranslation();

  const isLifetimeSelected =
    options.find(({ id }) => id === selectedOption)?.interval ===
    PriceInterval.Lifetime;

  return (
    <Styled.Card>
      <Styled.CardHeader>
        <Typography variant="h5" component="span">
          {t('dialogs.premium.title')}
        </Typography>
        <Styled.Badge>
          <Icon icon={Logo} />
        </Styled.Badge>
      </Styled.CardHeader>

      <FeatureList usps={lifetimeDealUsps} />

      <Styled.Plans>
        <PaymentOptions
          options={options}
          selectedOption={selectedOption}
          onOption={onOption}
          disabled={disabled}
          showSavingPercentage={false}
        />
        <Styled.PriceNotice>
          <Styled.PriceNoticeBadge></Styled.PriceNoticeBadge>
          <Trans
            i18nKey="forms.onboarding.premium.lifetime-deal.prices-going-up"
            components={{
              prices: (
                <Styled.PriceNoticeLink
                  key="prices"
                  href={NEW_PRICES_URL}
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </Styled.PriceNotice>
      </Styled.Plans>

      <Styled.CardFooter>
        {cta}
        <Styled.Disclaimer>
          {t(
            isLifetimeSelected
              ? 'dialogs.premium.interactions.disclaimer.lifetime'
              : 'dialogs.premium.interactions.disclaimer.title',
          )}
        </Styled.Disclaimer>
      </Styled.CardFooter>
    </Styled.Card>
  );
};
